import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '@chakra-ui/core'
import useLocaleText from 'components/useLocaleText'
import {
  RichTextEditor,
  EditorContent,
  getRichTextByteSize,
  MAX_RICH_TEXT_BYTES,
} from 'components/CommonComponents/RichTextEditor'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { useBuilderWidget } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'

interface VideoDescriptionProps {
  subtaskPath: string
}

export const VideoDescription: React.FC<VideoDescriptionProps> = ({ subtaskPath }) => {
  const { control, getValues, watch } = useFormContext()
  const builder = useBuilderWidget()
  const creationText = useCreationText()

  const t_description_placeholder = useLocaleText('t_say_something') || 'Add description...'

  const t_text_too_long = useLocaleText('text_too_long')

  const richTextValue = watch(`${subtaskPath}.content.rich_text`) as EditorContent | null

  const richTextError = useMemo(() => {
    const bytes = getRichTextByteSize(richTextValue)

    return bytes > MAX_RICH_TEXT_BYTES ? t_text_too_long || 'Text is too long (max 4KB)' : undefined
  }, [richTextValue, t_text_too_long])

  return (
    <>
      {builder && (
        <div className="creation-field-label creation-instructions-label">{creationText.videoInstructions}</div>
      )}
      <Controller
        name={`${subtaskPath}.content.rich_text`}
        control={control}
        as={RichTextEditor}
        toolbarVisibility="always"
        defaultValue={(getValues(`${subtaskPath}.content.rich_text`) as EditorContent) || null}
        placeholder={t_description_placeholder}
        containerStyle={{
          border: builder ? '1px solid #e2e5e8' : 'none',
          ...(builder ? { minHeight: '100px' } : {}),
          borderRadius: '8px',
          minHeight: '80px',
          boxShadow: richTextError ? '0px 0px 0px 3px #EA9999' : 'none',
        }}
        rules={{
          validate: (value: EditorContent) => {
            const bytes = getRichTextByteSize(value)

            return bytes <= MAX_RICH_TEXT_BYTES || t_text_too_long || 'Text is too long (max 4KB)'
          },
        }}
      />
      {richTextError && (
        <Box px="10px">
          <ErrorMessage errorMessage={richTextError} />
        </Box>
      )}
    </>
  )
}
